import { Col, Modal, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';

import {
  BlueButton,
  ErrorMessage,
  InfoTooltip,
  InitialCard,
  InitialPage,
  Scroll,
} from '@components';
import { AutoCompleteControlledInput } from '@components/inputs/AutoCompleteControlledInput';
import { LookerLog } from '@types';
import { creditHttp, gcpHttp } from '@network';
import { axiosErrorToString, dateFormatter, rbac } from '@utils';
import { Look } from './look';
import cls from './risk-ai-page.module.css';
import { useRedirect } from 'react-admin';
import { checkSuperadmin } from '@providers';

export const RiskAiPage = () => {
  const redirect = useRedirect();
  const [code, setCode] = useState('');
  const [logOpened, setLogOpened] = useState<LookerLog | null>(null);
  const [logs, setLogs] = useState<LookerLog[]>([]);
  const [loading, setLoading] = useState<string | boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(1);
  const [creditCodes, setCreditCodes] = useState<Record<number, string>>({});
  const isSuperadmin = checkSuperadmin();
  const readonly = rbac.hasRiskAiReadonly() && !isSuperadmin;
  const canEdit = rbac.hasRiskAiAll();

  useEffect(() => {
    (async () => {
      const nextLogs = await gcpHttp.getLogs();
      setLogs(nextLogs);

      const nextCreditCodes = await creditHttp.getCreditCodes();
      setCreditCodes(nextCreditCodes);
    })();

    if (!(isSuperadmin || readonly || canEdit)) {
      redirect('/');
    }
  }, []);

  const refreshData = () => void setVersion(prev => prev + 1);

  const runAction = async (url: string) => {
    if (loading) return;
    setLoading(url);
    const dto = { code };

    try {
      const log = await gcpHttp.runActionUrl(url, dto);
      setLoading(false);
      setError(null);
      setLogOpened(log);
      setLogs([log, ...logs]);
    } catch (error) {
      setLoading(false);
      setError(axiosErrorToString(error));
    }
  };

  const lookProps = { version, creditCodes };

  return (
    <InitialPage title="Risk AI" titleGap={10} className={cls.wrap}>
      <Row gutter={[20, 20]}>
        <Col span={15}>
          <InitialCard contentClass={cls.buttons}>
            <div className={cls.title}>
              <div className={cls.titleLeft}>
                <div>GCP Management Actions</div>
                <Tooltip />
              </div>
              <ErrorMessage text={error} large />
            </div>
            <Row gutter={[15, 15]}>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'batch-training'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('batch-training')}>
                  Batch Training
                </BlueButton>
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'scores-predict'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('scores-predict')}>
                  Scores Predict
                </BlueButton>
              </Col>
              <Col span={6}>
                <AutoCompleteControlledInput
                  value={code}
                  onChange={setCode}
                  reference="credit/codes-autocomplete"
                  placeholder="Credit Code"
                  disabled={readonly}
                />
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'show-meta-data'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('show-meta-data')}>
                  Show Meta Data
                </BlueButton>
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'fine-tuning'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('fine-tuning')}>
                  Fine Tuning
                </BlueButton>
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'predict'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('predict')}>
                  Predict
                </BlueButton>
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  disabled={!!loading || readonly}
                  onClick={refreshData}>
                  Refresh Data
                </BlueButton>
              </Col>
              <Col span={6}>
                <BlueButton className={cls.btn}
                  loading={loading === 'delete-gcs-objects'}
                  disabled={!!loading || readonly}
                  onClick={() => runAction('delete-gcs-objects')}>
                  Delete GCS Objects
                </BlueButton>
              </Col>
            </Row>
          </InitialCard>
        </Col>

        <Col span={9}>
          <InitialCard>
            <div className={cls.titleLeft}>GCP Logs</div>
            <Scroll height={95}>
              {logs.map(log => (
                <div key={log.id} className={cls.log} onClick={() => setLogOpened(log)}>
                  <div className={cls.logTitle}>
                    {log.errorJson ? <DeniedIcon /> : <OkIcon />}
                    <div>{log.title}</div>
                  </div>
                  <div className={cls.logInfo}>
                    <div className={cls.logAdmin}>{log.adminName}</div>
                    <div>&nbsp;|&nbsp;</div>
                    <div className={cls.logDate}>{dateFormatter.toDateTime(log.createdAt)}</div>
                  </div>
                </div>
              ))}
            </Scroll>
          </InitialCard>
        </Col>
      </Row>

      <InitialCard mt={20}>
        <div className={cls.titleLeft}>Credit Scoring</div>
        <Row gutter={[30, 0]} className={cls.reports}>
          <Col span={16}>
            <Look title="Preds Scores" lookId={1} {...lookProps} />
          </Col>
          <Col span={8}>
            <Look title="XGB Regressor Pred Score" lookId={2} {...lookProps} />
          </Col>
          <Col span={24}>
            <Look title="Decision Tree Scores Top 15 Variables" lookId={3} {...lookProps} />
          </Col>
          <Col span={24}>
            <Look title="XGB Results Top 15 Variables" lookId={4} {...lookProps} />
          </Col>
          <Col span={24}>
            <Look title="Extra Trees Scores Top 15 Variables" lookId={7} {...lookProps} />
          </Col>
          <Col span={24}>
            <Look title="Random Forest Scores Top 15 Variables" lookId={6} {...lookProps} />
          </Col>
          <Col span={24}>
            <Look title="Gradient Boosting Scores Top 15 Variables" lookId={5} {...lookProps} />
          </Col>
        </Row>
      </InitialCard>

      <Modal
        open={!!logOpened}
        title={null}
        footer={null}
        onCancel={() => setLogOpened(null)}
        width={1200}
        style={{ top: 60 }}
        bodyStyle={{ paddingLeft: 0, paddingRight: 0 }}
        closeIcon={<Close style={{ marginTop: 14 }} />}
      >
        <pre>
          {JSON.stringify(logOpened?.errorJson || logOpened?.responseJson || {}, null, 2)}
        </pre>
      </Modal>
    </InitialPage>
  );
};

const Tooltip = () => (
  <InfoTooltip white title={
    <>
      <div className={cls.tooltipTitle}>Endpoints Info</div>
      <ul className={cls.tooltipList}>
        <li>
          <b>Scores Predict:</b>
          <span>Predicts Risk scores using models from GCS.</span>
        </li>
        <li>
          <b>Batch Training:</b>
          <span>Trains models, uploads artifacts to GCS, metrics & feature
                        importance to GCS (JSON). Returns metrics & importance.</span>
        </li>
        <li>
          <b>Fine Tuning:</b>
          <span>Fine-tunes models from GCS, uploads new artifacts, metrics
                        & feature importance to GCS. Returns metrics & importance.
                        Requires base models.</span>
        </li>
        <li>
          <b>Predict:</b>
          <span>Predicts using a specific model from GCS. Results to BQ.
                        Requires the model to exist.</span>
        </li>
        <li>
          <b>Show Meta Data:</b>
          <span>Shows metadata about the data processing pipeline from BQ data.</span>
        </li>
        <li>
          <b>Delete GCS Objects:</b>
          <span>Deletes models or metrics from GCS.</span>
        </li>
      </ul>
    </>
  } />
);

const OkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" rx="10" fill="#275D3C" />
    <path d="M9.28577 11.9639L13.7501 7.49952"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
    <path d="M9.28577 11.9639L7.50003 10.1781"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
  </svg>
);

const DeniedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C4.47801 0 0 4.47801 0 10C0 15.522 4.47801 20 10 20C15.522 20 20 15.522 20 10C19.9911 4.47801 15.5175 0.00444247 10 0Z"
      fill="#FF4548" />
    <path d="M11.7132 9.80362L13.3947 11.5778C13.8684 12.0753 13.8684 12.8795 13.3947 13.3769C12.9211 13.8744 12.1553 13.8744 11.6816 13.3769L10 11.6027L8.31053 13.3686C7.84474 13.8661 7.07895 13.8661 6.60526 13.3686C6.13158 12.8712 6.13158 12.067 6.60526 11.5695L8.28684 9.80362L6.60526 8.02941C6.13158 7.54026 6.13158 6.73606 6.60526 6.23862C6.84211 5.9899 7.15 5.86554 7.45789 5.86554C7.76579 5.86554 8.07368 5.9899 8.31053 6.23862L10 8.00454L11.6895 6.23862C11.9263 5.9899 12.2342 5.86554 12.5421 5.86554C12.85 5.86554 13.1579 5.9899 13.3947 6.23862C13.8684 6.73606 13.8684 7.54026 13.3947 8.0377L11.7132 9.80362Z"
      fill="white" />
  </svg>
);
